import { createContext, useState } from "react";
import { useSelector } from "react-redux";
import usePredictiveText from "../Slides/Hooks/usePredictiveText";

// Context for the form
export const FormContext = createContext();

// Provider for the form context
export const FormContextProvider = ({ children }) => {
  const clientData = useSelector((state) => state.client);
  const [disableAll, setDisableAll] = useState(false);
  const [timeStamp, setTimeStamp] = useState(null);
  const [pageNumber, setPageNumber] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [requestStatus, setRequestStatus] = useState("Not Started");
  const [activeUserId, setActiveUserId] = useState(null);
  const [openCheckModal, setOpenCheckModal] = useState(false);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [isReady, setIsReady] = useState(false);
  const [openBackdrop, setOpenBackdrop] = useState(false);

  // Predictive Text Query
  const {
    data: {
      names,
      companies,
      titles,
      emails,
      phoneNumbers,
      regulatoryAgencies,
    } = {},
    isFetching: predictiveTextIsFetching,
    isFetched: predictiveTextFetched,
  } = usePredictiveText(clientData.id);

  // Form State Values for each Slide
  const [slide1, setSlide1] = useState({
    projectName: "",
    projectType: "",
    totAcr: "",
    distAcr: "",
    totalBldLots: "",
    totalUnits: "",
    residenceType: "",
    csw: "",
    landDeveloper: "",
  });

  const [slide2, setSlide2] = useState({
    safetyInterest: "",
    level1Safety: "",
    level2Safety: "",
  });

  const [slide3, setSlide3] = useState({
    swpppServicesRequest: "",
    noiRequest: false,
    noiFilingDate: "",
    notPreparationRequest: false,
    lvvChecklistRequest: false,
    swpppPreparationRequest: false,
    swpppInspectionRequest: false,
    postingNoticeSignRequest: false,
    streetSweeping: false,
    specialServices: "",
    permittee: "",
  });

  const [slide4, setSlide4] = useState({
    airQualityServicesRequest: "",
    filePermitRequest: false,
    amendPermitRequest: false,
    dustSignRequest: false,
    dustMonitoringRequest: false,
    wateringServiceRequest: false,
    dustAuditRequest: false,
    waterTruck: false,
    hydrantJonesValve: false,
    standTank: false,
    pond: false,
    well: false,
    fireHose: false,
    authorizationLetter: {
      id: "",
      signer: "",
      isSigned: false,
      letter: "",
      title: "",
      companyName: "",
    },
  });

  const [slide5, setSlide5] = useState({
    oName: "",
    oAdd: "",
    oPhone: "",
    oState: "",
    oCity: "",
    oZip: "",
    oFax: "",
    oTaxId: "",
    oContactName: "",
    oContactPhone: "",
    oContactEmail: "",
    oContactTitle: "",
    oAuthorizedContactName: "",
    oAuthorizedContactTitle: "",
  });

  const [slide6, setSlide6] = useState({
    sameAsOwner: "",
    gcName: "",
    gcAdd: "",
    gcPhone: "",
    gcState: "",
    gcCity: "",
    gcZip: "",
    gcFax: "",
    gcTaxId: "",
    gcContactName: "",
    gcContactPhone: "",
    gcContactEmail: "",
    gcContactTitle: "",
    gcAuthorizedContactName: "",
    gcAuthorizedContactTitle: "",
  });

  const [slide7, setSlide7] = useState({
    siteContactName: "",
    siteContactPhone: "",
    siteContactEmail: "",
    dustCertificationNumber: "",
    dustCertificationExpiration: "",
    projectManagerName: "",
    projectManagerCell: "",
    projectManagerPhone: "",
    projectManagerEmail: "",
  });

  const [slide8, setSlide8] = useState({
    startDate: "",
    endDate: "",
    offsites: "",
    offsiteDescription: "",
    activitiesDescription: "",
    stockpileStart: "",
    stockpileComplete: "",
    clearingGrubbingStart: "",
    clearingGrubbingComplete: "",
    gradingStart: "",
    gradingComplete: "",
    infrastructureStart: "",
    infrastructureComplete: "",
    pavingStart: "",
    pavingComplete: "",
    landscapingStart: "",
    landscapingComplete: "",
    verticalStart: "",
    verticalComplete: "",
    finalStabilizationStart: "",
    finalStabilizationComplete: "",
  });

  const [slide9, setSlide9] = useState({
    pAdd: "",
    pCity: "",
    pZip: "",
    pCounty: "",
    pState: "",
    bLat: "",
    bLng: "",
    eLat: "",
    eLng: "",
    isLinear: "",
    marker: { position: [0, 0], details: ["", "", "", ""] },
    marker2: { position: [0, 0], details: ["", "", "", ""] },
    parcelNumbers: "",
    crossRoad1: "",
    crossRoad2: "",
    cornerDirection: "",
  });

  const [slide10, setSlide10] = useState({
    billingCompany: "",
    billingAdd: "",
    billingCity: "",
    billingState: "",
    billingZip: "",
    billingPhone: "",
    billingFax: "",
    preferredContactMethod: "",
    billingContactName: "",
    billingContactPhone: "",
    billingContactEmail: "",
    billingContactTitle: "",
  });

  const [slide11, setSlide11] = useState({
    dustPermit: {
      name: "dustPermit",
      files: [],
    },
    areaOfDisturbance: {
      name: "areaOfDisturbance",
      files: [],
    },
    offsiteMap: {
      name: "offsiteMap",
      files: [],
    },
    gradingPlan: {
      name: "gradingPlan",
      files: [],
    },
  });

  const [slide12, setSlide12] = useState({
    submitterName: "",
    submitterTitle: "",
    submitterPhone: "",
    submitterEmail: "",
  });

  // Scroll to the input field and open the helper text
  const handleInputFocus = (ref, setOpenHelper) => {
    ref.current.scrollIntoView({
      behavior: "smooth",
      block: "center",
    });

    setOpenHelper(() => true);
  };

  // Disable input fields based on the request status and current userId/data.activeUserId
  const handleInputDisable = (idProp, state, disabledInput, userId) => {
    if (disableAll === true) return true;
    if (
      requestStatus === "IP" ||
      requestStatus === "I" ||
      requestStatus === "P"
    ) {
      return false;
    }
    if (requestStatus === "RV" && disabledInput === true) {
      return true;
    }
    return false;
  };

  // Get the invalid data for the input field
  function getInvalidData(inputName, invalidInputsData, slideNumber) {
    if (!invalidInputsData.data) return undefined;
    let invalidData = invalidInputsData.data.filter((obj) => {
      if (obj.slide === slideNumber && obj.name === inputName) {
        return obj;
      }
    })[0];

    return invalidData === undefined ? undefined : invalidData;
  }

  // Populate the invalid inputs for the slide
  const populateInvalidInputs = (
    invalidInputsData,
    disabledInputs,
    setDisabledInputs,
    slideNumber,
  ) => {
    if (
      requestStatus === "RV" &&
      invalidInputsData.isFetched &&
      invalidInputsData.data
    ) {
      let slidesInvalid = invalidInputsData.data.filter((obj) => {
        return obj.slide === slideNumber;
      });
      if (
        slidesInvalid.length > 0 &&
        !disabledInputs.includes(slidesInvalid[0].name)
      ) {
        slidesInvalid = slidesInvalid.map((obj) => obj.name);
        setDisabledInputs(() => [...slidesInvalid]);
      }
    }
  };

  // Gets the invalid input message
  const getInputMessage = (inputName, invalidInputsData, slideNumber) => {
    let message = "";
    if (invalidInputsData.isFetched && invalidInputsData.data) {
      let slidesInvalid = invalidInputsData.data.filter((obj) => {
        if (obj.slide === slideNumber && obj.name === inputName) {
          return obj;
        }
      });
      if (slidesInvalid.length > 0) {
        message = slidesInvalid[0].message;
      }
    }
    return message;
  };

  // Checks if an input should be disabled
  const disabledInputCheck = (inputName, disabledInputs) => {
    let disabled = true;
    if (disabledInputs.includes(inputName)) {
      disabled = false;
    }
    return disabled;
  };

  // Each slides progress status (used for timeline/color legend)
  const [slideStatus, setSlideStatus] = useState({
    slide1: "incomplete",
    slide2: "incomplete",
    slide3: "incomplete",
    slide4: "incomplete",
    slide5: "incomplete",
    slide6: "incomplete",
    slide7: "incomplete",
    slide8: "incomplete",
    slide9: "incomplete",
    slide10: "incomplete",
  });

  return (
    <FormContext.Provider
      value={{
        slide1,
        setSlide1,
        slide2,
        setSlide2,
        slide3,
        setSlide3,
        slide4,
        setSlide4,
        slide5,
        setSlide5,
        slide6,
        setSlide6,
        slide7,
        setSlide7,
        slide8,
        setSlide8,
        slide9,
        setSlide9,
        slide10,
        setSlide10,
        slide11,
        setSlide11,
        slide12,
        setSlide12,
        timeStamp,
        setTimeStamp,
        pageNumber,
        setPageNumber,
        isLoading,
        setIsLoading,
        names,
        companies,
        titles,
        emails,
        phoneNumbers,
        regulatoryAgencies,
        predictiveTextIsFetching,
        predictiveTextFetched,
        clientData,
        disableAll,
        setDisableAll,
        requestStatus,
        setRequestStatus,
        activeUserId,
        setActiveUserId,
        handleInputFocus,
        handleInputDisable,
        getInvalidData,
        populateInvalidInputs,
        getInputMessage,
        disabledInputCheck,
        openCheckModal,
        setOpenCheckModal,
        openSnackbar,
        setOpenSnackbar,
        snackbarMessage,
        setSnackbarMessage,
        snackbarSeverity,
        setSnackbarSeverity,
        isReady,
        setIsReady,
        slideStatus,
        setSlideStatus,
        openBackdrop,
        setOpenBackdrop,
      }}
    >
      {children}
    </FormContext.Provider>
  );
};
